import {
  clipTime,
  compileMotion,
  duckEnvelope,
  layerWindow,
  segmentGain,
  soundSegments,
  pieceRanks,
  type FxNode,
  type FxPlan,
  type ImageLayer,
  type MotionSplit,
  type TemplateDoc,
} from '@bannermatic/core';

/**
 * Plays the motion of a rendered <Scene> with the Web Animations API. All animations are created
 * paused and driven by `seek`, so a frame at time t is exactly the same in the editor preview and
 * in the video renderer (which seeks frame by frame).
 */

export interface MotionPlayer {
  /** Length of the video in ms. */
  readonly duration: number;
  readonly fps: number;
  time(): number;
  seek(ms: number): void;
  /** Real-time playback (editor preview). */
  play(opts?: { loop?: boolean; onTime?: (ms: number) => void; onEnd?: () => void }): void;
  pause(): void;
  readonly playing: boolean;
  /** Stop, remove all animations and undo text splitting. */
  destroy(): void;
}

interface SplitResult {
  pieces: HTMLElement[];
  ranks: number[];
  restore(): void;
}

const segmenter =
  typeof Intl !== 'undefined' && 'Segmenter' in Intl
    ? new (Intl as any).Segmenter(undefined, { granularity: 'grapheme' })
    : null;

function graphemes(s: string): string[] {
  if (segmenter) return Array.from(segmenter.segment(s), (x: any) => x.segment as string);
  return Array.from(s);
}

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

/** Wrap the words or letters of a text layer in inline-block spans that can be animated. */
function splitText(layerEl: HTMLElement, split: MotionSplit, seed: number): SplitResult | null {
  const textEl = layerEl.querySelector<HTMLElement>('.bm-text');
  if (!textEl || textEl.isContentEditable) return null;
  const text = textEl.textContent ?? '';
  if (!text.trim()) return null;

  // Each text node is split where it is, so accented words keep their span (and its colour);
  // the nodes React rendered are put back by `restore`
  const nodes: Text[] = [];
  const walker = document.createTreeWalker(textEl, NodeFilter.SHOW_TEXT);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) nodes.push(n as Text);
  const words: HTMLElement[] = [];
  const chars: HTMLElement[] = [];
  const replaced: Array<{ node: Text; parts: Node[] }> = [];
  for (const node of nodes) {
    const frag = document.createDocumentFragment();
    const parts: Node[] = [];
    for (const token of (node.data ?? '').split(/(\s+)/)) {
      if (!token) continue;
      if (/^\s+$/.test(token)) {
        const space = document.createTextNode(token);
        parts.push(space);
        frag.appendChild(space);
        continue;
      }
      const word = document.createElement('span');
      word.className = 'bm-piece';
      word.style.display = 'inline-block';
      word.style.whiteSpace = 'pre';
      if (split.by === 'char') {
        for (const g of graphemes(token)) {
          const ch = document.createElement('span');
          ch.className = 'bm-piece';
          ch.style.display = 'inline-block';
          ch.style.whiteSpace = 'pre';
          ch.textContent = g;
          word.appendChild(ch);
          chars.push(ch);
        }
      } else {
        word.textContent = token;
      }
      words.push(word);
      parts.push(word);
      frag.appendChild(word);
    }
    if (!parts.length) continue;
    replaced.push({ node, parts });
    node.replaceWith(frag);
  }

  let pieces: HTMLElement[];
  let ranks: number[];
  if (split.by === 'line') {
    // Words on the same line move together: rank = line index in the requested order
    const tops = words.map((w) => Math.round(w.offsetTop));
    const lines = [...new Set(tops)].sort((a, b) => a - b);
    const lineRanks = pieceRanks(lines.length, split.order, seed);
    pieces = words;
    ranks = tops.map((t) => lineRanks[lines.indexOf(t)]);
  } else {
    pieces = split.by === 'char' ? chars : words;
    ranks = pieceRanks(pieces.length, split.order, seed);
  }
  return {
    pieces,
    ranks,
    restore() {
      for (const { node, parts } of replaced) {
        const first = parts[0];
        if (first?.parentNode) first.parentNode.insertBefore(node, first);
        for (const p of parts) p.parentNode?.removeChild(p);
      }
    },
  };
}

/** Offset of a layer inside the scene, ignoring transforms (stack children included). */
function layoutBox(el: HTMLElement, root: HTMLElement) {
  let x = 0;
  let y = 0;
  let node: HTMLElement | null = el;
  while (node && node !== root) {
    x += node.offsetLeft;
    y += node.offsetTop;
    node = node.offsetParent as HTMLElement | null;
  }
  return { x, y, width: el.offsetWidth, height: el.offsetHeight };
}

const SVG_NS = 'http://www.w3.org/2000/svg';

function applyStyle(el: HTMLElement | SVGElement, style: Record<string, string | number>) {
  for (const [k, v] of Object.entries(style)) {
    if (v === '' || v === undefined || v === null) continue;
    const prop = k.startsWith('Webkit')
      ? `-webkit-${k
          .slice(6)
          .replace(/[A-Z]/g, (m) => '-' + m.toLowerCase())
          .replace(/^-/, '')}`
      : k.replace(/[A-Z]/g, (m) => '-' + m.toLowerCase());
    el.style.setProperty(prop, String(v));
  }
}

/** Shrink the font of a text box until its rows fit (measured without any animation applied). */
function fitText(el: HTMLElement) {
  let size = parseFloat(el.style.fontSize) || 16;
  const fits = () =>
    el.scrollHeight <= el.clientHeight + 1 &&
    Array.from(el.children).every((row) => (row as HTMLElement).scrollWidth <= el.clientWidth);
  for (let i = 0; i < 40 && size > 8 && !fits(); i++) {
    size *= 0.94;
    el.style.fontSize = `${size}px`;
  }
}

/**
 * Build the DOM of generated motion graphics and their (paused) animations. `insert` puts the
 * box into the scene; text boxes are fitted once laid out, before the animations start.
 */
function buildFx(
  plan: FxPlan,
  canvas: { width: number; height: number },
  total: number,
  animations: Animation[],
  insert: (box: HTMLElement) => void,
): HTMLElement {
  const box = document.createElement('div');
  box.className = 'bm-fx';
  box.dataset.fx = plan.kind;
  // The editor picks a graphic by clicking its text on the stage
  box.dataset.fxId = plan.id;
  applyStyle(box, {
    position: 'absolute',
    left: '0px',
    top: '0px',
    width: `${canvas.width}px`,
    height: `${canvas.height}px`,
    overflow: 'hidden',
    pointerEvents: 'none',
  });
  const pending: Array<() => void> = [];
  const fit: HTMLElement[] = [];
  const make = (node: FxNode, parent: HTMLElement) => {
    const el = document.createElement('div');
    applyStyle(el, node.style);
    if (node.text !== undefined) el.textContent = node.text;
    let path: SVGPathElement | null = null;
    if (node.path) {
      const svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('width', String(canvas.width));
      svg.setAttribute('height', String(canvas.height));
      svg.setAttribute('viewBox', `0 0 ${canvas.width} ${canvas.height}`);
      svg.style.position = 'absolute';
      svg.style.inset = '0';
      svg.style.overflow = 'visible';
      path = document.createElementNS(SVG_NS, 'path');
      path.setAttribute('d', node.path.d);
      path.setAttribute('fill', node.path.fill ?? 'none');
      path.setAttribute('stroke', node.path.stroke);
      path.setAttribute('stroke-width', String(node.path.width));
      path.setAttribute('stroke-linecap', node.path.cap ?? 'round');
      path.setAttribute('stroke-linejoin', 'round');
      // Normalised length: dash offset 1 → 0 draws the stroke
      path.setAttribute('pathLength', '1');
      path.style.strokeDasharray = '1 1';
      path.style.strokeDashoffset = '0';
      svg.appendChild(path);
      el.appendChild(svg);
    }
    for (const child of node.children ?? []) make(child, el);
    parent.appendChild(el);
    if (node.fit) fit.push(el);
    for (const a of node.anims ?? []) {
      const target = a.target === 'path' && path ? path : el;
      pending.push(() =>
        animations.push(
          target.animate(a.keyframes as Keyframe[], {
            delay: plan.start + a.delay,
            duration: Math.max(1, a.duration),
            easing: a.easing ?? 'linear',
            fill: a.fill ?? 'both',
            iterations: a.iterations ?? 1,
          }),
        ),
      );
    }
  };
  for (const node of plan.nodes) make(node, box);
  insert(box);
  for (const el of fit) fitText(el);
  for (const run of pending) run();
  // Only on screen while the item plays
  const s = Math.max(0, Math.min(1, plan.start / total));
  const e = Math.max(s, Math.min(1, (plan.start + plan.duration) / total));
  animations.push(
    box.animate(
      [
        { visibility: 'hidden', offset: 0 },
        { visibility: 'hidden', offset: s },
        { visibility: 'visible', offset: s },
        { visibility: 'visible', offset: e },
        { visibility: 'hidden', offset: e },
        { visibility: 'hidden', offset: 1 },
      ],
      { duration: total, fill: 'both', easing: 'linear' },
    ),
  );
  return box;
}

export function createMotionPlayer(
  root: HTMLElement,
  doc: TemplateDoc,
  /** `videos: false` leaves clips alone (the video capture engine draws them itself). */
  opts: { videos?: boolean } = {},
): MotionPlayer {
  const scene = root.matches('.bm-scene')
    ? root
    : (root.querySelector<HTMLElement>('.bm-scene') ?? root);
  const elOf = (id: string) =>
    scene.querySelector<HTMLElement>(`.bm-layer[data-layer-id="${CSS.escape(id)}"]`);
  const plan = compileMotion(doc, {
    boxOf: (id) => {
      const el = elOf(id);
      return el ? layoutBox(el, scene) : undefined;
    },
  });
  const animations: Animation[] = [];
  const splits: SplitResult[] = [];
  const total = Math.max(1, plan.duration);

  for (const layer of plan.layers) {
    const el = elOf(layer.layerId);
    if (!el) continue;
    const split =
      layer.split && layer.animations.some((a) => a.pieces)
        ? splitText(el, layer.split, hash(layer.layerId))
        : null;
    if (split) splits.push(split);

    for (const a of layer.animations) {
      const timing: KeyframeAnimationOptions = {
        duration: a.duration,
        easing: a.easing,
        fill: a.fill,
        iterations: a.iterations,
        composite: a.composite,
      };
      if (a.pieces && split) {
        const maxRank = Math.max(0, ...split.ranks);
        const stagger = layer.split?.stagger ?? 0;
        split.pieces.forEach((piece, i) => {
          const r = split.ranks[i];
          const delay = a.anchorEnd
            ? a.start - a.duration - (maxRank - r) * stagger
            : a.start + r * stagger;
          animations.push(piece.animate(a.keyframes as Keyframe[], { ...timing, delay }));
        });
      } else if (!a.pieces) {
        animations.push(el.animate(a.keyframes as Keyframe[], { ...timing, delay: a.start }));
      } else {
        // Text could not be split (empty): animate the whole box instead
        const delay = a.anchorEnd ? a.start - a.duration : a.start;
        animations.push(el.animate(a.keyframes as Keyframe[], { ...timing, delay }));
      }
    }

    if (layer.window) {
      const s = Math.max(0, Math.min(1, layer.window[0] / total));
      const e = Math.max(s, Math.min(1, layer.window[1] / total));
      animations.push(
        el.animate(
          [
            { visibility: 'hidden', offset: 0 },
            { visibility: 'hidden', offset: s },
            { visibility: 'visible', offset: s },
            { visibility: 'visible', offset: e },
            { visibility: 'hidden', offset: e },
            { visibility: 'hidden', offset: 1 },
          ],
          { duration: total, fill: 'both', easing: 'linear' },
        ),
      );
    }
  }

  // Motion graphics: under the layers (just above the background) and over them
  const fxNodes: HTMLElement[] = [];
  if (plan.fx.length) {
    const canvas = { width: doc.width, height: doc.height };
    const background = scene.querySelector(':scope > [data-background]');
    const watermark = scene.querySelector(':scope > [data-watermark]');
    const backAnchor = background ? background.nextSibling : scene.firstChild;
    for (const fx of plan.fx) {
      const el = buildFx(fx, canvas, total, animations, (box) =>
        scene.insertBefore(box, fx.z === 'back' ? backAnchor : watermark),
      );
      fxNodes.push(el);
    }
  }

  for (const a of animations) a.pause();

  // Video clips follow the playhead: seeked when paused, played along (and nudged back into
  // sync when they drift) during playback
  const clips: Array<{
    el: HTMLVideoElement;
    video: NonNullable<ImageLayer['video']>;
    window: [number, number];
  }> = [];
  if (opts.videos !== false) {
    const byId = new Map(doc.layers.map((l) => [l.id, l]));
    scene.querySelectorAll<HTMLVideoElement>('video[data-bm-video]').forEach((el) => {
      const l = byId.get(el.dataset.bmVideo ?? '');
      if (l?.type === 'image' && l.video) {
        el.pause();
        el.muted = l.video.muted;
        el.volume = Math.max(0, Math.min(1, l.video.volume));
        el.playbackRate = l.video.speed || 1;
        // A clip starts playing with its scene
        clips.push({ el, video: l.video, window: layerWindow(doc, l) });
      }
    });
  }
  // Music, voice-over and effects follow the playhead the same way, with fades and ducking
  const segments = soundSegments(doc);
  const duck = duckEnvelope(doc, segments);
  const sounds: Array<{ el: HTMLAudioElement; seg: (typeof segments)[number] }> = [];
  if (opts.videos !== false) {
    const byId = new Map(segments.map((x) => [x.id, x]));
    scene.querySelectorAll<HTMLAudioElement>('audio[data-bm-sound]').forEach((el) => {
      const seg = byId.get(el.dataset.bmSound ?? '');
      if (!seg) return;
      el.pause();
      sounds.push({ el, seg });
    });
  }
  const follow = (el: HTMLMediaElement, want: number, active: boolean, running: boolean) => {
    if (!running || !active) {
      if (!el.paused) el.pause();
      if (Math.abs(el.currentTime - want) > 0.01) el.currentTime = want;
      return;
    }
    if (Math.abs(el.currentTime - want) > 0.2) el.currentTime = want;
    // Sound may be refused without a user gesture: carry on silently
    if (el.paused)
      void el.play().catch(() => {
        el.muted = true;
        void el.play().catch(() => {});
      });
  };
  const syncMusic = (ms: number, running: boolean) => {
    for (const { el, seg } of sounds) {
      const local = Math.max(0, Math.min(seg.duration, ms - seg.start));
      el.volume = segmentGain(seg, ms, duck);
      follow(
        el,
        (seg.mediaStart + local * seg.rate) / 1000,
        ms >= seg.start && ms < seg.start + seg.duration,
        running,
      );
    }
  };
  const syncClips = (ms: number, running: boolean) => {
    for (const { el, video, window } of clips) {
      follow(
        el,
        clipTime(video, ms - window[0]) / 1000,
        ms >= window[0] && ms < window[1],
        running,
      );
    }
  };

  let current = 0;
  let raf = 0;
  let playing = false;

  const seek = (ms: number) => {
    current = Math.max(0, Math.min(total, ms));
    for (const a of animations) a.currentTime = current;
    syncClips(current, playing);
    syncMusic(current, playing);
  };
  seek(0);

  const stop = () => {
    playing = false;
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    syncClips(current, false);
    syncMusic(current, false);
  };

  return {
    duration: plan.duration,
    fps: plan.fps,
    time: () => current,
    seek,
    get playing() {
      return playing;
    },
    play(opts = {}) {
      stop();
      playing = true;
      let last = performance.now();
      if (current >= total) seek(0);
      syncClips(current, true);
      syncMusic(current, true);
      const tick = (now: number) => {
        if (!playing) return;
        let t = current + (now - last);
        last = now;
        if (t >= total) {
          if (opts.loop) t = t % total;
          else {
            seek(total);
            opts.onTime?.(total);
            stop();
            opts.onEnd?.();
            return;
          }
        }
        seek(t);
        opts.onTime?.(t);
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    },
    pause: stop,
    destroy() {
      stop();
      for (const a of animations) a.cancel();
      animations.length = 0;
      for (const s of splits) s.restore();
      splits.length = 0;
      for (const n of fxNodes) n.remove();
      fxNodes.length = 0;
      for (const { el } of clips) el.pause();
      for (const { el } of sounds) el.pause();
    },
  };
}
